import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../hooks/useTranslation';

const PhotoCPanel = () => {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('Events');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [fileDescriptions, setFileDescriptions] = useState({});
  const [publishStatus, setPublishStatus] = useState(true);
  const [useAI, setUseAI] = useState(true); // Enable AI descriptions by default
  const [loading, setLoading] = useState(true);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [filterPublished, setFilterPublished] = useState(null); // null = all, true = published, false = unpublished
  const [message, setMessage] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const DEFAULT_CATEGORIES = ['Events', 'Programs', 'Community', 'Staff', 'Facilities', 'Outreach'];

  // Fetch photos
  const fetchPhotos = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterPublished !== null) params.append('is_published', filterPublished);
      
      const response = await fetch(`${BACKEND_URL}/api/photos?${params.toString()}`);
      const data = await response.json();
      if (data.success) {
        setPhotos(data.photos);
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
    }
  }, [BACKEND_URL, filterPublished]);

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/photos/categories`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/photos/stats`);
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPhotos(), fetchCategories(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchPhotos]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > 50) {
      showMessage('Maximum 50 files allowed per upload', 'error');
      return;
    }

    setSelectedFiles(files);
    
    // Create previews
    const previews = [];
    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        previews[index] = reader.result;
        if (previews.filter(p => p).length === files.length) {
          setFilePreviews([...previews]);
        }
      };
      reader.readAsDataURL(file);
    });

    // Initialize descriptions
    const descriptions = {};
    files.forEach((file, index) => {
      descriptions[index] = '';
    });
    setFileDescriptions(descriptions);
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );

    if (files.length > 50) {
      showMessage('Maximum 50 files allowed per upload', 'error');
      return;
    }

    handleFileSelect({ target: { files } });
  };

  // Upload photos
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      showMessage('Please select files to upload', 'error');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      
      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });
      
      formData.append('category', selectedCategory);
      formData.append('descriptions', JSON.stringify(Object.values(fileDescriptions)));
      formData.append('is_published', publishStatus);
      formData.append('use_ai', useAI);

      const response = await fetch(`${BACKEND_URL}/api/photos/upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      
      if (data.success || data.uploaded > 0) {
        showMessage(`Successfully uploaded ${data.uploaded} photos!`, 'success');
        setSelectedFiles([]);
        setFilePreviews([]);
        setFileDescriptions({});
        await Promise.all([fetchPhotos(), fetchCategories(), fetchStats()]);
      } else {
        showMessage(`Upload failed: ${data.errors?.[0]?.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showMessage('Upload failed. Please try again.', 'error');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Update photo
  const handleUpdatePhoto = async (photoId, updates) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/photos/${photoId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      const data = await response.json();
      
      if (data.success) {
        showMessage('Photo updated successfully!', 'success');
        setEditingPhoto(null);
        await Promise.all([fetchPhotos(), fetchCategories(), fetchStats()]);
      }
    } catch (error) {
      console.error('Update error:', error);
      showMessage('Update failed. Please try again.', 'error');
    }
  };

  // Delete photo
  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/photos/${photoId}`, {
        method: 'DELETE',
      });

      const data = await response.json();
      
      if (data.success) {
        showMessage('Photo deleted successfully!', 'success');
        await Promise.all([fetchPhotos(), fetchCategories(), fetchStats()]);
      }
    } catch (error) {
      console.error('Delete error:', error);
      showMessage('Delete failed. Please try again.', 'error');
    }
  };

  // Show message
  const showMessage = (text, type = 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  // Filter photos by category
  const filteredPhotos = selectedCategory === 'All' 
    ? photos 
    : photos.filter(p => p.category === selectedCategory);

  return (
    <div className="space-y-6">
      {/* Message Banner */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-100 text-green-800' :
          message.type === 'error' ? 'bg-red-100 text-red-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {message.text}
        </div>
      )}

      {/* Header with Stats */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">📸 Photo Gallery Management</h2>
        
        {stats && (
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600">{stats.total_photos}</div>
              <div className="text-sm text-gray-600">Total Photos</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600">{stats.published_photos}</div>
              <div className="text-sm text-gray-600">Published</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-yellow-600">{stats.unpublished_photos}</div>
              <div className="text-sm text-gray-600">Unpublished</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-600">{stats.total_views}</div>
              <div className="text-sm text-gray-600">Total Views</div>
            </div>
          </div>
        )}
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">📤 Upload Photos</h3>
        
        {/* Category, Publish Status & AI Toggle */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent"
            >
              {DEFAULT_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={publishStatus}
              onChange={(e) => setPublishStatus(e.target.value === 'true')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent"
            >
              <option value="true">✅ Published (Visible on site)</option>
              <option value="false">📝 Unpublished (Hidden)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              AI Descriptions
            </label>
            <select
              value={useAI}
              onChange={(e) => setUseAI(e.target.value === 'true')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent"
            >
              <option value="true">🤖 AI Enhanced (Recommended)</option>
              <option value="false">✍️ Manual Only</option>
            </select>
          </div>
        </div>

        {/* AI Description Help Text */}
        {useAI && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <span className="text-blue-600 text-xl">💡</span>
              <div className="text-sm text-blue-800">
                <strong>AI-Enhanced Descriptions:</strong> Add a short hint (e.g., "Sunday service", "youth event", "radio program") 
                and AI will generate a detailed, professional description for each photo automatically.
              </div>
            </div>
          </div>
        )}

        {/* Drag & Drop Zone */}
        <div
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-kioo-primary transition-colors cursor-pointer"
          onClick={() => document.getElementById('file-input').click()}
        >
          <input
            id="file-input"
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          <div className="text-6xl mb-4">📁</div>
          <p className="text-lg font-medium text-gray-700 mb-2">
            Drag & drop photos here or click to browse
          </p>
          <p className="text-sm text-gray-500">
            Up to 50 images • JPG, PNG, WebP • Max 5MB each
          </p>
        </div>

        {/* Selected Files Preview */}
        {selectedFiles.length > 0 && (
          <div className="mt-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              Selected Photos ({selectedFiles.length})
            </h4>
            
            <div className="grid grid-cols-3 md:grid-cols-5 gap-4 max-h-96 overflow-y-auto">
              {filePreviews.map((preview, index) => (
                <div key={index} className="relative group">
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity rounded-lg flex items-center justify-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const newFiles = selectedFiles.filter((_, i) => i !== index);
                        const newPreviews = filePreviews.filter((_, i) => i !== index);
                        setSelectedFiles(newFiles);
                        setFilePreviews(newPreviews);
                      }}
                      className="opacity-0 group-hover:opacity-100 text-white bg-red-500 rounded-full p-2 hover:bg-red-600 transition"
                    >
                      🗑️
                    </button>
                  </div>
                  
                  {/* Description Input */}
                  <textarea
                    placeholder={useAI ? "Add hint (e.g., 'church service', 'radio program')..." : "Add full description..."}
                    value={fileDescriptions[index] || ''}
                    onChange={(e) => {
                      setFileDescriptions({
                        ...fileDescriptions,
                        [index]: e.target.value
                      });
                    }}
                    className="w-full mt-2 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-kioo-primary focus:border-transparent"
                    rows="2"
                  />
                  
                  <p className="text-xs text-gray-500 mt-1 truncate">
                    {selectedFiles[index]?.name}
                  </p>
                </div>
              ))}
            </div>

            {/* Upload Button */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="px-6 py-3 bg-kioo-primary text-white font-semibold rounded-lg hover:bg-kioo-primary/90 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {uploading ? '⏳ Uploading...' : `📤 Upload ${selectedFiles.length} Photo${selectedFiles.length > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Photo Gallery */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-900">🖼️ Photo Gallery</h3>
          
          {/* Filters */}
          <div className="flex gap-4">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
            >
              <option value="All">All Categories</option>
              {DEFAULT_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            <select
              value={filterPublished === null ? 'all' : filterPublished.toString()}
              onChange={(e) => {
                const val = e.target.value;
                setFilterPublished(val === 'all' ? null : val === 'true');
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
            >
              <option value="all">All Photos</option>
              <option value="true">Published Only</option>
              <option value="false">Unpublished Only</option>
            </select>
          </div>
        </div>

        {/* Photo Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-gray-600">Loading photos...</p>
          </div>
        ) : filteredPhotos.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">📷</div>
            <p className="text-gray-600">No photos found. Upload some to get started!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredPhotos.map((photo) => (
              <div key={photo.id} className="relative group">
                <img
                  src={`${BACKEND_URL}${photo.url}`}
                  alt={photo.description || 'Photo'}
                  className="w-full h-40 object-cover rounded-lg"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-70 transition-opacity rounded-lg flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 flex gap-2">
                    <button
                      onClick={() => setEditingPhoto(photo)}
                      className="text-white bg-blue-500 rounded-full p-2 hover:bg-blue-600 transition"
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeletePhoto(photo.id)}
                      className="text-white bg-red-500 rounded-full p-2 hover:bg-red-600 transition"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="absolute top-2 right-2">
                  {photo.is_published ? (
                    <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">✅ Live</span>
                  ) : (
                    <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full">📝 Draft</span>
                  )}
                </div>

                {/* Info */}
                <div className="mt-2">
                  <p className="text-xs text-gray-600 font-medium">{photo.category}</p>
                  {photo.description && (
                    <p className="text-xs text-gray-500 line-clamp-2">{photo.description}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">👁️ {photo.views} views</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Edit Photo</h3>
              
              <img
                src={`${BACKEND_URL}${editingPhoto.url}`}
                alt="Preview"
                className="w-full h-64 object-contain bg-gray-100 rounded-lg mb-4"
              />

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    defaultValue={editingPhoto.description}
                    id="edit-description"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    defaultValue={editingPhoto.category}
                    id="edit-category"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
                  >
                    {DEFAULT_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    defaultValue={editingPhoto.is_published.toString()}
                    id="edit-published"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
                  >
                    <option value="true">✅ Published (Visible on site)</option>
                    <option value="false">📝 Unpublished (Hidden)</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  onClick={() => setEditingPhoto(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleUpdatePhoto(editingPhoto.id, {
                      description: document.getElementById('edit-description').value,
                      category: document.getElementById('edit-category').value,
                      is_published: document.getElementById('edit-published').value === 'true'
                    });
                  }}
                  className="px-4 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-primary/90 transition"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoCPanel;
